// 02 — Buy your chicks. A budget, a straight-run/sexed choice, one row per
// breed the sim has numbers for, and the Veteran switch with its honest copy
// (REFOCUS §3).
//
// The draft travels to screen 03 in the route params, so a back tap restores
// the exact basket instead of re-asking everything.

import type { Breed, HousingType } from '../../core/types';
import { t } from '../../i18n';
import { budgetBar } from '../components/bar';
import { chip } from '../components/chip';
import { segmented } from '../components/segmented';
import { stepper } from '../components/stepper';
import { toggle } from '../components/toggle';
import { button, div, h, link } from '../dom';
import {
  seasonChickBudget,
  seasonSexedHenRate,
  seasonSexedMultiplier,
} from '../../sim/season';
import { flockCapFor } from '../../sim/spots';
import type { BreedOption } from '../selectors';
import { selectBreedCatalog } from '../selectors';
import type { RouteParams, Screen, ScreenContext } from '../router';
import { chicksArriveScreen } from './chicksArrive';

export interface Draft {
  counts: Partial<Record<Breed, number>>;
  sexed: boolean;
  veteran: boolean;
}

function readDraft(params: RouteParams, catalog: BreedOption[], housing: HousingType): Draft {
  const draft: Draft = {
    counts: {},
    sexed: false,
    veteran: params.veteran === '1',
  };
  const fromParams = params.counts;
  if (fromParams) {
    // A basket arriving from a back tap is clamped to the cap for the mode it was
    // built in, which is the mode the Veteran switch put in the route.
    const cap = flockCapFor(housing, draft.veteran);
    try {
      const parsed = JSON.parse(fromParams) as Record<string, number>;
      for (const option of catalog) {
        const value = Math.max(0, Math.min(cap, Math.floor(Number(parsed[option.breed] ?? 0))));
        if (value > 0) draft.counts[option.breed] = value;
      }
    } catch {
      // A hand-edited URL is not worth an error screen: start from an empty
      // basket and let the player fill it.
    }
  }
  draft.sexed = params.sexed === '1';
  return draft;
}

function encodeDraft(draft: Draft): string {
  return JSON.stringify(draft.counts);
}

export function basketCost(draft: Draft, catalog: BreedOption[]): number {
  let total = 0;
  for (const option of catalog) {
    const count = draft.counts[option.breed] ?? 0;
    const price = draft.sexed
      ? Math.round(option.price * seasonSexedMultiplier())
      : option.price;
    total += count * price;
  }
  return total;
}

export interface BasketView {
  cost: number;
  /** The season's opening-basket allowance (REFOCUS §5). */
  total: number;
  chickCount: number;
  /** The most birds this home and mode take, from the rule the sim enforces. */
  cap: number;
  capLeft: number;
  overBudget: boolean;
  overCap: boolean;
  canBuy: boolean;
}

/**
 * What this basket costs, what is left of the allowance, and whether the home
 * and mode picked on the screens before this one will take the birds.
 *
 * The cap is `flockCapFor` and not a number typed here, because it is the one
 * the store refuses against (`coop_full`, sim/store.ts) and the one the
 * housing card promises. Onboarding was the third reader, with its own
 * hardcoded `balcony ? 3 : 6`, so a Normal balcony could be handed four hens
 * against a cap of three. Over the cap is refused, never trimmed: a silent trim
 * would sell fewer chicks than the screen just asked the player to name.
 */
export function basketView(
  draft: Draft,
  catalog: BreedOption[],
  housing: HousingType,
): BasketView {
  const cost = basketCost(draft, catalog);
  const total = seasonChickBudget();
  const chickCount = Object.values(draft.counts).reduce((sum, n) => sum + (n ?? 0), 0);
  const cap = flockCapFor(housing, draft.veteran);
  return {
    cost,
    total,
    chickCount,
    cap,
    capLeft: Math.max(0, cap - chickCount),
    overBudget: cost > total,
    overCap: chickCount > cap,
    canBuy: cost <= total && chickCount > 0 && chickCount <= cap,
  };
}

function breedRow(
  option: BreedOption,
  draft: Draft,
  budgetLeft: number,
  capLeft: number,
  onCount: (next: number) => void,
): HTMLElement {
  const count = draft.counts[option.breed] ?? 0;
  const active = count > 0;
  const unitPrice = draft.sexed
    ? Math.round(option.price * seasonSexedMultiplier())
    : option.price;

  const art = div({ class: 'cf-breed__art' });
  if (option.needsArt || !option.sprite) {
    art.appendChild(h('span', { class: 'cf-breed__noart', text: t('buy.artNeeded') }));
  } else {
    art.appendChild(
      h('img', { class: 'cf-breed__img', attrs: { src: option.sprite, alt: t(option.nameKey) } }),
    );
  }

  // The role tag sits on its own line: on a long name like Rhode Island Red the
  // mockup's inline tag pushed under the stepper (docs/ui/README.md, 02).
  const name = h(
    'div',
    { class: 'cf-breed__nameline' },
    h('span', { class: 'cf-breed__name', text: t(option.nameKey) }),
  );
  // Two bars, not three: eggs and temperament are real, and hardiness has no
  // scale to draw (sim/breedProfile.ts). A breed no research covers has no
  // temperament either, and its row is left out rather than filled in.
  const temperamentBars =
    option.bars.temperament === null
      ? []
      : [
          h('span', { class: 'cf-tiny', text: t('buy.statTemperament') }),
          barRow(option.bars.temperament),
        ];
  const stats = div(
    { class: 'cf-breed__stats' },
    h('span', { class: 'cf-tiny', text: t('buy.statEggs') }),
    barRow(option.bars.eggs),
    ...temperamentBars,
  );

  return div(
    { class: `cf-breed${active ? ' cf-breed--on' : ''}` },
    art,
    div(
      { class: 'cf-breed__body' },
      name,
      chip({ text: t(option.role), tone: 'good', compact: true }),
      stats,
      h('span', {
        class: 'cf-breed__price',
        text: t('buy.priceEach', { price: unitPrice }),
      }),
    ),
    stepper({
      addLabelKey: 'buy.addBreed',
      removeLabelKey: 'buy.removeBreed',
      name: t(option.nameKey),
      value: count,
      // The + is the cheapest way to see the "over budget" state, so it stops
      // at the cap and at whatever the remaining money covers.
      max: Math.max(count, Math.min(capLeft, count + budgetLeft)),
      onChange: onCount,
    }),
  );
}

function barRow(value: number): HTMLElement {
  return h(
    'span',
    { class: 'cf-breed__bar' },
    h('span', {
      class: 'cf-breed__barfill',
      style: { width: `${Math.max(0, Math.min(100, value))}%` },
    }),
  );
}

export function buyChicksScreen(): Screen<HTMLElement> {
  return {
    id: 'buyChicks',
    render: (ctx: ScreenContext<HTMLElement>) => {
      const housing = (ctx.params.housing as HousingType) ?? 'backyard';
      const catalog = selectBreedCatalog();
      // Onboarding runs before there is a game, so the cap comes from the
      // housing and mode the player is choosing here, read from the sim's own
      // rule rather than from a number this screen owns.
      let draft = readDraft(ctx.params, catalog, housing);
      // An empty basket is unhelpful; the mockup opens on two Silkies' worth of
      // spend so the budget bar means something on the first screen.
      if (Object.keys(draft.counts).length === 0) {
        draft = { ...draft, counts: { [catalog[0]?.breed ?? 'silkie']: 1 } };
      }

      const screen = div({ class: 'cf-screen cf-screen--buy' });
      const head = div(
        { class: 'cf-screen__head' },
        link({
          class: 'cf-btn cf-btn--link',
          text: t('common.back'),
          on: { click: () => ctx.router.back() },
        }),
        h('span', { class: 'cf-eyebrow', text: t('onboard.step', { n: '2', of: '5' }) }),
      );
      const list = div({ class: 'cf-stack cf-stack--tight' });
      const budgetHost = div({});
      const veteranHost = div({});
      const foot = div({ class: 'cf-screen__foot' });

      const repaint = (): void => {
        const view = basketView(draft, catalog, housing);
        budgetHost.replaceChildren(
          budgetBar({
            label: t('buy.budget'),
            spent: view.cost,
            total: view.total,
            unit: t('common.eggits'),
            side:
              view.chickCount === 1
                ? t('buy.chickCountOne')
                : t('buy.chickCount', { count: view.chickCount }),
          }),
        );

        list.replaceChildren();
        for (const option of catalog) {
          const unitPrice = draft.sexed
            ? Math.round(option.price * seasonSexedMultiplier())
            : option.price;
          const affordable = view.total - view.cost;
          list.appendChild(
            breedRow(option, draft, Math.max(0, Math.floor(affordable / Math.max(1, unitPrice))), view.capLeft, (next) => {
              const counts = { ...draft.counts };
              if (next <= 0) delete counts[option.breed];
              else counts[option.breed] = next;
              draft = { ...draft, counts };
              repaint();
            }),
          );
        }

        veteranHost.replaceChildren(
          toggle({
            label: t('buy.veteranTitle'),
            description: draft.veteran
              ? t('buy.veteranOn')
              : t('buy.veteranOff'),
            pressed: draft.veteran,
            tone: 'danger',
            onChange: (next) => {
              draft = { ...draft, veteran: next };
              repaint();
            },
          }),
        );

        const go = button({
          class: 'cf-btn cf-btn--primary',
          text: t('buy.bringHome'),
          attrs: { disabled: !view.canBuy },
          on: {
            click: () => {
              ctx.router.push(chicksArriveScreen(), {
                housing,
                counts: encodeDraft(draft),
                sexed: draft.sexed ? '1' : '0',
                veteran: draft.veteran ? '1' : '0',
              });
            },
          },
        });
        foot.replaceChildren(go);
        if (view.overBudget) {
          foot.prepend(h('p', { class: 'cf-overbudget', text: t('buy.overBudget') }));
        }
        if (view.overCap) {
          foot.prepend(h('p', { class: 'cf-overbudget', text: t('buy.overCap') }));
        }
      };

      screen.append(
        head,
        h('h1', { class: 'cf-display cf-display--sm', text: t('buy.title') }),
        budgetHost,
        segmented<'straight' | 'sexed'>({
          label: t('buy.sexedLabel'),
          value: draft.sexed ? 'sexed' : 'straight',
          options: [
            { value: 'straight', label: t('buy.straightRun'), hint: t('buy.straightRunHint') },
            {
              value: 'sexed',
              label: t('buy.sexed', { rate: Math.round(seasonSexedHenRate() * 100) }),
              hint: t('buy.sexedHint'),
            },
          ],
          onChange: (value) => {
            draft = { ...draft, sexed: value === 'sexed' };
            repaint();
          },
        }),
        div({ class: 'cf-grow cf-scroll cf-stack cf-stack--tight' }, list),
        h('p', { class: 'cf-legend', text: t('buy.legend') }),
        veteranHost,
        foot,
      );
      repaint();
      screen.tabIndex = -1;
      return screen;
    },
  };
}
